import secureStorage from '@xpos/utils/src/secure.storage.utils';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { addBreadcrumb } from '@sentry/vue';

const RETRY_METHODS = ['get', 'head', 'options']; // Only retry idempotent requests
const MAX_RETRY = 3;
const isRetryableError = (err: AxiosError) => {
  if (err.response) return false; // Server responded, don't retry
  const code = err.code || '';
  if (['ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED'].includes(code) || code.includes('TIMEOUT')) return false; // Timeout/cancel - don't retry
  return true; // Network connection errors only
};

interface RetryConfig extends InternalAxiosRequestConfig { _retry?: number; }

export const createRequest = (url?: string) => {
  const instance = axios.create({
    baseURL: url || import.meta.env.VITE_BASE_URL,
    timeout: 30000,
    headers: { Authorization: 'Bearer ' + secureStorage.getAccessToken() },
  });
  instance.interceptors.response.use(res => res, async (err: AxiosError) => {
    const config = err.config as RetryConfig;
    const method = config?.method?.toLowerCase();
    // Retry only idempotent requests on network errors, up to MAX_RETRY times with exponential backoff
    const retryCount = config._retry ?? 0;
    if (method && RETRY_METHODS.includes(method) && isRetryableError(err) && retryCount < MAX_RETRY) {
      config._retry = retryCount + 1;
      if (config._retry > 1) await new Promise(r => setTimeout(r, (config._retry! - 1) * 500));
      return instance.request(config);
    }
    // Log 5xx errors to Sentry breadcrumb for endpoint identification
    const status = err.response?.status;
    if (status && status >= 500) addBreadcrumb({ type: 'http', category: 'axios.error', level: 'error', data: { url: config?.url, method: config?.method, status } });
    return Promise.reject(err);
  });
  return instance;
};

// Non-blocking HEAD request to trigger SignalR sensitive data push
export const triggerSensitiveDataSync = () => {
  const merchantUid = secureStorage.getItem('merchant_uid');
  const deviceUid = secureStorage.getItem('device_uid');
  if (!merchantUid || !deviceUid) return;
  createRequest().head(`/api/v5/merchants/${merchantUid}/sync_sensitive_data`, {
    headers: { 'x-device-uid': deviceUid },
  }).catch(() => {});
};
